import React, { useCallback, useEffect, useState } from "react";
import { debounce } from "lodash";
import { useDispatch } from "react-redux";
import SelectField from "../../forms/select-field";
import FromTo from "../../../assets/icons/from-to";
import InputField from "../../forms/input-field";
import { useFormikContext } from "formik";
import ItemsSelectField from "../../forms/items-select-field";
import ItemsInputField from "../../forms/items-input-field";
import moment from "moment";
import AddButton, { DraftButton } from "../../buttons/button";
import errorToast from "../../helper-components/error-toast";
import warningToast from "../../helper-components/warning-toast";
import successToast from "../../helper-components/success-toast";
import {
  stockOutGrnItemsAction,
  stockOutPurchaseInvoiceItemsAction,
} from "../../../redux/actions/purchase-action";
import {
  warehouseExpiredItemsListAction,
  warehouseGetIdAction,
} from "../../../redux/actions/inventory-action";
import { searchBinLocationAction } from "../../../redux/actions/general-action";
import { isAdmin } from "../../../redux/config";
import {
  buildExpiredItemRows,
  findUnsplittableBatches,
  splitQuantityAcrossStores,
  unsplittableMessage,
} from "../../../utils/stock-transfer-calculations";

const StocktransferHeadSec = ({
  warehouseList = [],
  warehouse = "",
  binLocationList = [],
  disabled = false,
  initialData = {},
}) => {
  const { values, setFieldValue } = useFormikContext();
  const dispatch = useDispatch();
  const [loadingGrn, setLoadingGrn] = useState(false);
  const [loadingExpiry, setLoadingExpiry] = useState(false);
  const isInStoreTransfer =
    values?.stock_management_action === "in_store_transfer";
  const stockOutSource = values?.stock_out_source || "grn";
  const stockOutSourceLabel =
    stockOutSource === "purchase_invoice" ? "Purchase Invoice" : "GRN";

  // Default the stock out source to GRN so the select never shows empty
  useEffect(() => {
    if (
      values?.stock_management_action === "stock_out" &&
      !values?.stock_out_source
    ) {
      setFieldValue("stock_out_source", "grn");
    }
  }, [values?.stock_management_action, values?.stock_out_source]);

  // Build source options: gray out any already-selected destination for Stock IN / Transfer Request
  const sourceOptions = warehouseList.map((opt) => {
    const shouldDisable =
      (values?.stock_management_action === "stock_in" ||
        values?.stock_management_action === "stock_transfer_request") &&
      values?.to_warehouse_id?.some((sel) => sel?.value === opt.value);
    return shouldDisable ? { ...opt, isDisabled: true } : opt;
  });

  const isDestinationLocked =
    disabled ||
    values?.stock_management_action === "stock_in" ||
    values?.stock_management_action === "in_store_transfer" ||
    values?.stock_management_action === "stock_transfer_request";

  // Build destination options: gray out any already-selected source for Stock OUT
  const destinationOptions = warehouseList.map((opt) => {
    const shouldDisable =
      values?.stock_management_action === "stock_out" &&
      values?.from_warehouse_id?.some((sel) => sel?.value === opt.value);
    return shouldDisable ? { ...opt, isDisabled: true } : opt;
  });

  // Wrap the logged-in warehouse id as a single-item multi-select option
  const warehouseOption = (id) => {
    if (!id) return [];
    const match = warehouseList?.find((opt) => opt?.value === id);
    return [match ?? { label: id, value: id }];
  };

  // Handle date validation on blur
  const handleDateBlur = (e) => {
    console.log("Date blur triggered:", e.target.value); // Debug log
    const inputDate = e.target.value;
    const today = moment().format("YYYY-MM-DD");

    // If no input date, set to today's date
    if (!inputDate) {
      console.log("No input date, setting to today:", today);
      setFieldValue("posting_date", today);
      return;
    }

    // Check if date format is valid (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(inputDate)) {
      console.log("Invalid format, setting to today:", today);
      setFieldValue("posting_date", today);
      return;
    }

    // Check if date is valid using moment
    const inputMoment = moment(inputDate, "YYYY-MM-DD", true);
    const todayMoment = moment(today, "YYYY-MM-DD");

    // If date is invalid, set to today's date
    if (!inputMoment.isValid()) {
      console.log("Invalid date, setting to today:", today);
      setFieldValue("posting_date", today);
      return;
    }

    // If date is in the past, set to today's date
    if (inputMoment.isBefore(todayMoment)) {
      console.log("Past date, setting to today:", today);
      setFieldValue("posting_date", today);
      return;
    }

    // Save the valid future date
    console.log("Valid date, saving:", inputDate);
    setFieldValue("posting_date", inputDate);
  };

  // Fetch a GRN / purchase invoice by its invoice number and prefill the item
  // table with its items/batches
  const handleLoadGrn = () => {
    if (!values?.stock_out_invoice_number) {
      errorToast(`Please enter a ${stockOutSourceLabel} Invoice Number`);
      return;
    }
    const invoiceNumber = values.stock_out_invoice_number;
    const source = stockOutSource;
    const sourceLabel = stockOutSourceLabel;
    const loadItemsAction =
      source === "purchase_invoice"
        ? stockOutPurchaseInvoiceItemsAction
        : stockOutGrnItemsAction;
    setLoadingGrn(true);
    dispatch(
      loadItemsAction(
        invoiceNumber,
        (res) => {
          setLoadingGrn(false);
          const items = res?.data?.itemList || [];
          if (items.length === 0) {
            warningToast(`No items found for this ${sourceLabel} invoice number`);
            return;
          }

          const mappedItems = items.map((item) => {
            const matchedUom = item?.uom_dropdown?.find(
              (u) => u?.item_id === item?.item_uom_id
            );
            const baseQuantity = parseFloat(matchedUom?.base_quantity) || 1;

            return {
              sl_no: 1,
              item_name: item?.item_name || item?.item_code || "",
              item_code: item?.item_code || "",
              item_uom: item?.item_uom || "",
              quantity: item?.item_qty ?? 0,
              item_batches: (item?.batch_details || []).map((batch) => ({
                item_id: batch?.item_id,
                item_batch_number: batch?.item_batch_number,
                item_batch_quantity: batch?.item_batch_qty ?? 0,
                item_batch_free_quantity: 0,
                item_batch_purchase_rate: batch?.item_purchase_rate ?? 0,
                item_batch_unit_price: batch?.item_unit_price ?? 0,
                item_batch_discount_percentage: 0,
                item_batch_discount_amount: 0,
                from_bin_id: batch?.item_location ?? "",
                from_bin_location: batch?.item_location_name ?? "",
                to_bin_id: "",
                to_bin_location: "",
                item_exp_date: batch?.item_exp_date
                  ? moment(batch.item_exp_date).format("YYYY-MM-DD")
                  : "",
                created_date: "",
                remainingQty: 0,
              })),
              current_avail: {
                item_uom_source: "",
                item_uom_destination: "",
              },
              item_id: item?.item_id || "",
              item_uom_id: item?.item_uom_id || "",
              base_quantity: baseQuantity,
              uom_dropdown: (item?.uom_dropdown || []).map((uom) => ({
                item_uom_id: uom?.item_id,
                label: uom?.alt_uom,
                value: uom?.alt_uom,
                base_quantity: uom?.base_quantity,
              })),
              total_sellable_quantity: item?.item_available_qty ?? 0,
              base_total_sellable_quantity: item?.item_available_qty ?? 0,
              from_warehouse_id: "",
              from_warehouse_name: "",
              to_warehouse_id: "",
              to_warehouse_name: "",
            };
          });

          setFieldValue("data", [...mappedItems, initialData]);
          successToast(
            `Loaded ${mappedItems.length} item(s) from ${sourceLabel} ${invoiceNumber}`
          );
        },
        (err) => {
          setLoadingGrn(false);
          errorToast(
            err?.data?.message ||
              err?.message ||
              `Failed to load ${sourceLabel} items`
          );
        }
      )
    );
  };

  // Move To Bin searches bins on the server as the user types.
  const fetchBinOptions = (inputValue, callback) => {
    if (!inputValue?.length) {
      callback([]);
      return;
    }
    dispatch(
      searchBinLocationAction(
        { field: "bin_location_no", value: inputValue },
        (data) =>
          callback(
            (data?.data ?? []).map((bin) => ({
              label: bin?.bin_location_no,
              value: bin?.bin_no_id,
              actualLabel: bin?.bin_location_no,
            }))
          ),
        () => callback([])
      )
    );
  };
  const loadBinOptions = useCallback(
    debounce((inputValue, callback) => fetchBinOptions(inputValue, callback), 500),
    []
  );

  // Load every batch in this store expiring within the entered number of days.
  // In Store Transfer also needs the bin the stock is being moved into.
  const handleLoadExpiry = () => {
    const days = Number(values?.expiry_days);
    if (
      values?.expiry_days === undefined ||
      values?.expiry_days === "" ||
      !Number.isInteger(days) ||
      days < 0
    ) {
      errorToast("Please enter the number of days as a whole number");
      return;
    }
    const toBin =
      isInStoreTransfer && values?.expiry_to_bin_id
        ? {
            value: values.expiry_to_bin_id,
            actualLabel: values?.expiry_to_bin_location ?? "",
          }
        : null;
    if (isInStoreTransfer && !toBin) {
      errorToast("Please select the bin to move the items to");
      return;
    }
    setLoadingExpiry(true);
    dispatch(
      warehouseGetIdAction(
        {},
        (res) => {
          const warehouseId = res?.data?.pos_id;
          if (!warehouseId) {
            setLoadingExpiry(false);
            errorToast("Failed to fetch warehouse");
            return;
          }
          loadExpiryItems(warehouseId, days, toBin);
        },
        (err) => {
          setLoadingExpiry(false);
          errorToast(err?.data?.message || "Failed to fetch warehouse");
        }
      )
    );
  };

  const loadExpiryItems = (warehouseId, days, toBin) => {
    dispatch(
      warehouseExpiredItemsListAction(
        { warehouse_id: warehouseId, days },
        (res) => {
          setLoadingExpiry(false);
          const mappedItems = buildExpiredItemRows(res?.data || [], {
            toBinId: toBin?.value ?? "",
            toBinLocation: toBin?.actualLabel ?? "",
          });
          if (mappedItems.length === 0) {
            warningToast(`No items expiring within ${days} day(s)`);
            return;
          }
          setFieldValue("data", [...mappedItems, initialData]);
          successToast(
            `Loaded ${mappedItems.length} item(s) expiring within ${days} day(s)`
          );
        },
        (err) => {
          setLoadingExpiry(false);
          errorToast(
            err?.data?.message || err?.message || "Failed to load expiry items"
          );
        }
      )
    );
  };

  // Split each item's quantity across the selected destination stores. The
  // grid can only show one number per batch, so it shows the SMALLEST share —
  // the submit is what hands the leftover units to the last stores (43 over 2
  // stores sends 21 then 22), which is why the untouched total is kept on the
  // batch for it to re-split from.
  const handleSplitEqually = () => {
    const storeCount = values?.to_warehouse_id?.length || 0;
    if (storeCount < 1) {
      warningToast("Select at least one destination store before splitting");
      return;
    }

    // Every destination store has to get at least one unit of every batch —
    // a batch holding fewer units than stores can't be split, so name those
    // items and leave the grid untouched. The request is remembered so Save
    // keeps refusing until the quantities or stores are fixed.
    setFieldValue("split_equally_requested", true);
    const unsplittable = findUnsplittableBatches(values?.data, storeCount);
    if (unsplittable.length > 0) {
      errorToast(unsplittableMessage(storeCount, unsplittable));
      return;
    }

    let splitAny = false;
    const updatedData = (values?.data || []).map((item) => {
      const batches = item?.item_batches || [];
      if (batches.length === 0) return item;

      const updatedBatches = batches.map((batch) => {
        const originalQty =
          batch?.original_item_batch_quantity ??
          batch?.item_batch_quantity ??
          0;
        const shares = splitQuantityAcrossStores(originalQty, storeCount);
        const smallestShare = shares[0] ?? 0;
        return {
          ...batch,
          original_item_batch_quantity: originalQty,
          // What this split wrote into the grid. The submit only re-splits a
          // batch whose quantity still matches it — editing the quantity by
          // hand afterwards makes the typed number win instead of being
          // silently overwritten by the old total.
          split_base_quantity: smallestShare,
          item_batch_quantity: smallestShare,
        };
      });
      const newQuantity = updatedBatches.reduce(
        (sum, batch) => sum + (parseFloat(batch.item_batch_quantity) || 0),
        0
      );
      splitAny = true;
      return { ...item, item_batches: updatedBatches, quantity: newQuantity };
    });

    if (!splitAny) {
      warningToast("Add items with batch details before splitting equally");
      return;
    }
    setFieldValue("data", updatedData);
    successToast(
      `Quantities split equally across ${storeCount} store${storeCount > 1 ? "s" : ""
      }`
    );
  };

  const showStockOutLoadRow =
    isAdmin && !disabled && values?.stock_management_action === "stock_out";
  const showSelectedDestinations = values?.to_warehouse_id?.length > 1;
  // Selected destination stores with a remove (×) for each; shown beside
  // Split Equally when that row is visible, otherwise on a row of their own
  const selectedDestinationChips = showSelectedDestinations && (
    <>
      <span className="text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]">
        Selected Destinations ({values.to_warehouse_id.length}):
      </span>
      {values.to_warehouse_id.map((store) => (
        <span
          key={store?.value}
          className="flex items-center gap-2 px-2 py-[2px] rounded-[5px] bg-[#E5ECFF] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)]"
        >
          {store?.label}
          {!isDestinationLocked && (
            <button
              type="button"
              title={`Remove ${store?.label}`}
              className="text-[#2820DA] leading-none"
              onClick={() =>
                setFieldValue(
                  "to_warehouse_id",
                  values.to_warehouse_id.filter(
                    (sel) => sel?.value !== store?.value
                  )
                )
              }
            >
              ×
            </button>
          )}
        </span>
      ))}
    </>
  );

  return (
    <>
      <div className="min-h-[8rem] flex items-center w-full">
        <div className="bg-[var(--add-billing-head-bg)] border-radius h-[87px] flex items-center px-3 w-full gap-4">
          <div className="w-[16%]">
            <ItemsSelectField
              handleSelect={(selectedOption) => {
                if (
                  selectedOption?.value === "stock_in" ||
                  selectedOption?.value === "stock_transfer_request"
                ) {
                  setFieldValue("to_warehouse_id", warehouseOption(warehouse));
                  setFieldValue("from_warehouse_id", []);
                } else if (selectedOption?.value === "stock_out") {
                  setFieldValue("from_warehouse_id", warehouseOption(warehouse));
                  setFieldValue("to_warehouse_id", []);
                } else if (selectedOption?.value === "in_store_transfer") {
                  setFieldValue("from_warehouse_id", warehouseOption(warehouse));
                  setFieldValue("to_warehouse_id", warehouseOption(warehouse));
                }
                setFieldValue("data", [initialData]);
              }}
              options={stockActionList}
              placeholder="Select Action"
              isRequired={true}
              labelClass="min-h-[1.2rem] text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]"
              inputClass="min-h-[40px] px-2 border border-[var(--search-input-border)] focus:outline-none focus:border-[var(--search-input-focus)] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)] border-radius input-field-no-spin-buttons"
              type="text"
              disabled={disabled}
              name="stock_management_action"
              label="Stock Management Action"
              isLabelReq={true}
            />
          </div>
          <div className="w-[16%]">
            <SelectField
              key={
                "src-" +
                values?.stock_management_action +
                "-" +
                (values?.to_warehouse_id?.map((v) => v?.value).join(",") ?? "")
              }
              isMulti={true}
              isContainer={false}
              parentClass="flex flex-col gap-1 w-full"
              options={sourceOptions}
              placeholder="Select Source"
              // Only one source is allowed — keep the array shape the rest of
              // the form expects, but replace instead of adding
              handleSelected={(selected) => {
                if (Array.isArray(selected) && selected.length > 1) {
                  setFieldValue("from_warehouse_id", selected.slice(-1));
                }
              }}
              isRequired={true}
              label="Source (from)"
              labelClass="min-h-[1.2rem] text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]"
              defaultInputClass="min-h-[40px] px-2 border border-[var(--search-input-border)] focus:outline-none focus:border-[var(--search-input-focus)] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)] border-radius input-field-no-spin-buttons"
              errorBorder="border border-[var(--search-input-border)]"
              inputBorder="border border-[var(--search-input-border)]"
              selectClass="w-full"
              errMsgContainerClass="hidden"
              labelsLimit={1}
              style={{
                valueContainer: (base) => ({
                  ...base,
                  flexWrap: "nowrap",
                  overflow: "hidden",
                }),
              }}
              isSearchable={true}
              isDisabled={
                disabled ||
                values?.stock_management_action === "stock_out" ||
                values?.stock_management_action === "in_store_transfer"
              }
              name="from_warehouse_id"
            />
          </div>
          <div className="w-[16%]">
            <SelectField
              key={
                "dest-" +
                values?.stock_management_action +
                "-" +
                (values?.from_warehouse_id?.map((v) => v?.value).join(",") ?? "")
              }
              isMulti={true}
              isContainer={false}
              parentClass="flex flex-col gap-1 w-full"
              options={destinationOptions}
              placeholder="Select Destination"
              isRequired={true}
              label="Destination (to)"
              labelClass="min-h-[1.2rem] text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]"
              defaultInputClass="min-h-[40px] px-2 border border-[var(--search-input-border)] focus:outline-none focus:border-[var(--search-input-focus)] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)] border-radius input-field-no-spin-buttons"
              errorBorder="border border-[var(--search-input-border)]"
              inputBorder="border border-[var(--search-input-border)]"
              selectClass="w-full"
              errMsgContainerClass="hidden"
              labelsLimit={1}
              style={{
                valueContainer: (base) => ({
                  ...base,
                  flexWrap: "nowrap",
                  overflow: "hidden",
                }),
              }}
              isSearchable={true}
              hideSelectedOptions={false}
              closeMenuOnSelect={false}
              isDisabled={isDestinationLocked}
              name="to_warehouse_id"
            />
          </div>
          <div className="w-[10%]">
            <ItemsInputField
              isRequired={false}
              labelClass="min-h-[1.2rem] text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]"
              inputClass="min-h-[40px] px-2 border border-[var(--search-input-border)] focus:outline-none focus:border-[var(--search-input-focus)] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)] border-radius input-field-no-spin-buttons"
              type="date"
              disabled={disabled}
              name={`posting_date`}
              label="Transfer Date"
              isLabelReq={true}
              // min={moment().format("YYYY-MM-DD")}
              onChange={() => { }}
              onBlur={handleDateBlur}
            />
          </div>
          <div className="w-[40%]">
            <ItemsInputField
              alphanumericOnly={true}
              maxLength={50}
              placeholder="Enter Reason for Stock Transfer"
              isRequired={false}
              labelClass="min-h-[1.2rem] text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]"
              inputClass="min-h-[40px] px-2 border border-[var(--search-input-border)] focus:outline-none focus:border-[var(--search-input-focus)] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)] border-radius input-field-no-spin-buttons"
              type="text"
              disabled={disabled}
              name={`stock_transfer_reason`}
              label="Reason"
              allowAll={true}
              isLabelReq={true}
            />
          </div>
        </div>
      </div>
      {showSelectedDestinations && !showStockOutLoadRow && (
        <div className="flex flex-wrap items-center w-full gap-2 px-3 mb-4">
          {selectedDestinationChips}
        </div>
      )}
      {showStockOutLoadRow && (
        <div className="flex items-end w-full gap-4 px-3 mb-4">
          <div className="w-[200px]">
            <ItemsSelectField
              handleSelect={(selectedOption) => {
                if (selectedOption?.value !== stockOutSource) {
                  setFieldValue("stock_out_invoice_number", "");
                }
              }}
              options={stockOutSourceList}
              placeholder="Select Load From"
              labelClass="min-h-[1.2rem] text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]"
              disabled={disabled}
              name="stock_out_source"
              label="Load From"
              isLabelReq={true}
            />
          </div>
          <div className="w-[280px]">
            <ItemsInputField
              allowAll={true}
              maxLength={50}
              placeholder={`Enter ${stockOutSourceLabel} Invoice Number`}
              isRequired={false}
              labelClass="min-h-[1.2rem] text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]"
              inputClass="min-h-[40px] px-2 border border-[var(--search-input-border)] focus:outline-none focus:border-[var(--search-input-focus)] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)] border-radius input-field-no-spin-buttons"
              type="text"
              disabled={disabled}
              name="stock_out_invoice_number"
              label={`${stockOutSourceLabel} Invoice Number`}
              isLabelReq={true}
            />
          </div>
          <div className="flex gap-3">
            <AddButton
              prefixIcon={false}
              text={`Load ${stockOutSourceLabel}`}
              onclkFn={handleLoadGrn}
              loading={loadingGrn}
              isDisabled={loadingGrn}
              type="button"
              className="flex items-center justify-center px-[20px] h-[40px] whitespace-nowrap rounded-md gap-[8px] bg-[var(--primary-button-bg-color)] cursor-pointer text-[var(--primary-button-fg-color)] border border-[var(--primary-button-bg-color)] hover:bg-[var(--primary-button-hover-color)] text-[length:var(--primary-button-font-size)] font-[var(--primary-button-font-weight)]"
            />
            <DraftButton
              text={"Split Equally"}
              onclkFn={handleSplitEqually}
              type="button"
              className="flex items-center justify-center px-[20px] h-[40px] whitespace-nowrap rounded-md gap-[8px] bg-transparent cursor-pointer text-[var(--primary-button-bg-color)] border border-[var(--primary-button-bg-color)] hover:bg-[var(--add-billing-head-bg)] text-[length:var(--primary-button-font-size)] font-[var(--primary-button-font-weight)]"
            />
          </div>
          {showSelectedDestinations && (
            <div className="flex flex-wrap items-center flex-1 min-h-[40px] gap-2">
              {selectedDestinationChips}
            </div>
          )}
        </div>
      )}
      {!isAdmin &&
        !disabled &&
        (values?.stock_management_action === "stock_out" ||
          isInStoreTransfer) && (
        <div className="flex items-end w-full gap-4 px-3 mb-4">
          <div className="w-[180px]">
            <ItemsInputField
              placeholder="Enter Days"
              isRequired={false}
              labelClass="min-h-[1.2rem] text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]"
              inputClass="min-h-[40px] px-2 border border-[var(--search-input-border)] focus:outline-none focus:border-[var(--search-input-focus)] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)] border-radius input-field-no-spin-buttons"
              type="number"
              disabled={disabled}
              name="expiry_days"
              label="Expiring Within (Days)"
              isLabelReq={true}
            />
          </div>
          {isInStoreTransfer && (
            <div className="w-[220px]">
              <SelectField
                isContainer={false}
                parentClass="flex flex-col gap-1 w-full"
                loadOptions={loadBinOptions}
                defaultOptions={false}
                defaultValue={
                  values?.expiry_to_bin_id
                    ? {
                        label: values?.expiry_to_bin_location ?? "",
                        value: values.expiry_to_bin_id,
                      }
                    : null
                }
                handleSelected={(sel) =>
                  setFieldValue("expiry_to_bin_location", sel?.actualLabel ?? "")
                }
                placeholder="Search Bin"
                isRequired={true}
                label="Move To Bin"
                labelClass="min-h-[1.2rem] text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]"
                defaultInputClass="min-h-[40px] px-2 border border-[var(--search-input-border)] focus:outline-none focus:border-[var(--search-input-focus)] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)] border-radius input-field-no-spin-buttons"
                errorBorder="border border-[var(--search-input-border)]"
                inputBorder="border border-[var(--search-input-border)]"
                selectClass="w-full"
                errMsgContainerClass="hidden"
                isSearchable={true}
                isDisabled={disabled}
                name="expiry_to_bin_id"
              />
            </div>
          )}
          <AddButton
            prefixIcon={false}
            text={"Load Expiry Items"}
            onclkFn={handleLoadExpiry}
            loading={loadingExpiry}
            isDisabled={loadingExpiry}
            type="button"
            className="flex items-center justify-center px-[20px] h-[40px] whitespace-nowrap rounded-md gap-[8px] bg-[var(--primary-button-bg-color)] cursor-pointer text-[var(--primary-button-fg-color)] border border-[var(--primary-button-bg-color)] hover:bg-[var(--primary-button-hover-color)] text-[length:var(--primary-button-font-size)] font-[var(--primary-button-font-weight)]"
          />
        </div>
      )}
    </>
  );
};

export default StocktransferHeadSec;

export const stockActionList = [
  {
    label: "Stock IN",
    value: "stock_in",
  },
  {
    label: "Stock OUT",
    value: "stock_out",
  },
  {
    label: "IN Store Transfer",
    value: "in_store_transfer",
  },
  {
    label: "Stock Transfer Request",
    value: "stock_transfer_request",
  },
];

export const stockOutSourceList = [
  {
    label: "Goods Receipt Note",
    value: "grn",
  },
  {
    label: "Purchase Invoice",
    value: "purchase_invoice",
  },
];
